/**
 * CustomerDetailsModal — drill-down on a dashboard analytics tile.
 *
 * Renders a side-sheet / bottom-sheet that lists the recent rows for the
 * clicked source (scans, menu_views, requests, shoutouts, reviews, events).
 *
 * Anonymous sources (scans / menu_views / reviews) show an empathetic note
 * that guest identities aren't captured for that flow + a CTA to enable
 * WhatsApp OTP for richer attribution going forward.
 *
 * Events source has its own grouped view (registrations by event).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  X, Loader2, Phone, User, Music, MessageSquareQuote, Calendar,
  ShieldOff, CheckCircle2, Clock, ExternalLink, Star,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_META = {
  scans:       { title: 'QR Scans',       color: '#FF007F', mayBeAnonymous: true,  icon: User },
  menu_views:  { title: 'Menu Views',     color: '#00FFFF', mayBeAnonymous: true,  icon: User },
  requests:    { title: 'Song Requests',  color: '#39FF14', mayBeAnonymous: false, icon: Music },
  shoutouts:   { title: 'Shoutouts',      color: '#FF8C00', mayBeAnonymous: false, icon: MessageSquareQuote },
  reviews:     { title: 'Reviews',        color: '#FFD700', mayBeAnonymous: true,  icon: Star },
  events:      { title: 'Event Sign-ups', color: '#9D00FF', mayBeAnonymous: false, icon: Calendar },
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    if (diffMin < 60 * 24 * 7) return `${Math.round(diffMin / (60 * 24))}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const CustomerDetailsModal = ({ source, onClose }) => {
  const meta = SOURCE_META[source] || SOURCE_META.scans;
  const Icon = meta.icon;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openEventId, setOpenEventId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const url = source === 'events'
          ? `${API}/analytics/dashboard/events-details`
          : `${API}/analytics/dashboard/details?source=${encodeURIComponent(source)}&limit=100`;
        const res = await axios.get(url);
        if (mounted) setData(res.data);
      } catch (err) {
        console.warn('[CustomerDetailsModal] load failed:', err);
        if (mounted) setData({ items: [], groups: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [source]);

  // Set the first event group open by default for snappier scan
  useEffect(() => {
    if (source === 'events' && data?.groups?.length && !openEventId) {
      setOpenEventId(data.groups[0].event.id);
    }
  }, [source, data, openEventId]);

  const itemsCount = useMemo(() => {
    if (source === 'events') return data?.total_registrations ?? 0;
    return data?.items?.length ?? 0;
  }, [source, data]);

  return (
    <motion.div
      data-testid="customer-details-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center sm:justify-center sm:p-6"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className="w-full sm:max-w-2xl max-h-[92vh] bg-[#0a0a0a] border border-white/10 rounded-t-[28px] sm:rounded-[24px] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${meta.color}18`, boxShadow: `inset 0 0 0 1px ${meta.color}40` }}
            >
              <Icon className="w-5 h-5" style={{ color: meta.color }} strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-white font-extrabold text-lg font-['Outfit'] leading-tight">{meta.title}</h3>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono']">
                {loading ? 'Loading…' : `${itemsCount.toLocaleString()} ${source === 'events' ? 'registrations' : 'rows'}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white p-2 rounded-lg hover:bg-white/5"
            data-testid="customer-details-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 content-scroll" data-testid="customer-details-body">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: meta.color }} />
            </div>
          ) : source === 'events' ? (
            <EventGroups
              groups={data?.groups || []}
              openId={openEventId}
              onToggle={(id) => setOpenEventId((cur) => (cur === id ? null : id))}
            />
          ) : meta.mayBeAnonymous && data?.anonymous ? (
            // Backend confirmed ZERO identities across the returned rows —
            // show the "enable OTP" nudge banner AND the anonymous rows.
            <AnonymousList items={data?.items || []} source={source} color={meta.color} />
          ) : meta.mayBeAnonymous ? (
            // Mixed: some rows have identity, some don't. Renderer decides
            // per row. No top-of-list nudge because OTP IS working.
            <MixedList items={data?.items || []} source={source} color={meta.color} />
          ) : (
            <NamedList items={data?.items || []} source={source} color={meta.color} />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ---------- Anonymous list (scans / menu_views / reviews) ----------
const AnonymousList = ({ items, source, color }) => {
  if (!items.length) {
    return (
      <EmptyState
        title="No activity yet"
        body="Nothing to show here. Once guests start interacting, you'll see traffic patterns."
      />
    );
  }
  return (
    <>
      <div className="flex items-start gap-2 p-3 mb-4 rounded-xl bg-white/[0.03] border border-white/8">
        <ShieldOff className="w-4 h-4 text-yellow-400/80 mt-0.5 flex-shrink-0" />
        <p className="text-[12px] text-gray-300 leading-relaxed">
          <span className="text-yellow-400/90 font-semibold">Anonymous traffic.</span>{' '}
          This flow doesn&apos;t capture guest name or phone. To collect identities, enable{' '}
          <span className="text-white">WhatsApp OTP</span> in Settings → WhatsApp.
        </p>
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li
            key={it.id}
            className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
            data-testid={`anonymous-row-${it.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}10` }}
              >
                <User className="w-4 h-4" style={{ color }} />
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  Anonymous visitor
                </p>
                <p className="text-gray-500 text-[11px] truncate font-['JetBrains_Mono']">
                  {it.extra?.ip_address ? `IP ${maskIp(it.extra.ip_address)}` : '—'}
                  {it.extra?.session_code ? ` · ${it.extra.session_code}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] text-gray-500 font-['JetBrains_Mono']">
                {formatWhen(it.created_at)}
              </span>
              {source === 'reviews' && it.extra?.pills?.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-end max-w-[180px]">
                  {it.extra.pills.slice(0, 3).map((p) => (
                    <span
                      key={p}
                      className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFD700]/15 text-[#FFD700] uppercase tracking-wider"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

// ---------- Mixed list (scans / menu_views / reviews with SOME identities) ----------
// Renders identity-attached rows the same way NamedList does + anonymous rows
// beneath. No top-of-list "enable OTP" nudge — OTP is clearly working.
const MixedList = ({ items, source, color }) => {
  if (!items.length) {
    return <EmptyState title="No activity yet" body="Once guests interact, you'll see them here." />;
  }
  return (
    <ul className="space-y-2">
      {items.map((it) => {
        const hasIdentity = !!(it.name || it.phone);
        return hasIdentity ? (
          <IdentityRow key={it.id} it={it} source={source} color={color} />
        ) : (
          <AnonymousRow key={it.id} it={it} source={source} color={color} />
        );
      })}
    </ul>
  );
};

const IdentityRow = ({ it, source, color }) => (
  <li
    className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
    data-testid={`identity-row-${it.id}`}
  >
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <span className="text-sm font-extrabold">
          {(it.name || '?').charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-white text-sm font-semibold truncate font-['Outfit']">
            {it.name || 'Guest'}
          </p>
          {it.verified && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-bold bg-[#39FF14]/15 text-[#39FF14] flex items-center gap-0.5"
              data-testid={`identity-verified-${it.id}`}
              title="Verified via WhatsApp OTP"
            >
              <CheckCircle2 className="w-2.5 h-2.5" /> Verified
            </span>
          )}
        </div>
        {it.phone && (
          <a
            href={`https://wa.me/${cleanPhoneForWa(it.phone)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-[#25D366] hover:underline font-['JetBrains_Mono']"
            onClick={(e) => e.stopPropagation()}
            data-testid={`identity-phone-${it.id}`}
          >
            <Phone className="w-3 h-3" />
            {it.phone}
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        )}
        {source === 'reviews' && it.extra?.review_text && (
          <p className="text-gray-400 text-xs mt-1 italic line-clamp-2">
            “{it.extra.review_text}”
          </p>
        )}
      </div>
    </div>
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      <span className="text-[10px] text-gray-500 font-['JetBrains_Mono']">
        {formatWhen(it.created_at)}
      </span>
      {source === 'reviews' && it.extra?.pills?.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-end max-w-[180px]">
          {it.extra.pills.slice(0, 3).map((p) => (
            <span
              key={p}
              className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFD700]/15 text-[#FFD700] uppercase tracking-wider"
            >
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  </li>
);

const AnonymousRow = ({ it, source, color }) => (
  <li
    className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
    data-testid={`anonymous-row-${it.id}`}
  >
    <div className="flex items-center gap-3 min-w-0">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}10` }}
      >
        <User className="w-4 h-4" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-white text-sm font-medium truncate">Anonymous visitor</p>
        <p className="text-gray-500 text-[11px] truncate font-['JetBrains_Mono']">
          {it.extra?.ip_address ? `IP ${maskIp(it.extra.ip_address)}` : '—'}
          {it.extra?.session_code ? ` · ${it.extra.session_code}` : ''}
        </p>
      </div>
    </div>
    <div className="flex flex-col items-end gap-1">
      <span className="text-[10px] text-gray-500 font-['JetBrains_Mono']">
        {formatWhen(it.created_at)}
      </span>
      {source === 'reviews' && it.extra?.pills?.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-end max-w-[180px]">
          {it.extra.pills.slice(0, 3).map((p) => (
            <span
              key={p}
              className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFD700]/15 text-[#FFD700] uppercase tracking-wider"
            >
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  </li>
);

// ---------- Named list (requests / shoutouts) ----------
const NamedList = ({ items, source, color }) => {
  if (!items.length) {
    return <EmptyState title="No entries yet" body="Once guests engage, their details will show here." />;
  }
  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li
          key={it.id}
          className="p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
          data-testid={`customer-row-${it.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}15`, color }}
              >
                <span className="text-sm font-extrabold">
                  {(it.name || '?').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-semibold truncate font-['Outfit']">
                  {it.name || 'Unknown'}
                </p>
                {it.phone && (
                  <a
                    href={`https://wa.me/${cleanPhoneForWa(it.phone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-[#25D366] hover:underline font-['JetBrains_Mono']"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-3 h-3" />
                    {it.phone}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                )}
                {source === 'requests' && it.extra?.track_name && (
                  <p className="text-gray-400 text-xs mt-1 truncate">
                    🎵 <span className="text-gray-200">{it.extra.track_name}</span>
                    {Array.isArray(it.extra.track_artists) && it.extra.track_artists.length > 0
                      ? ` — ${it.extra.track_artists.join(', ')}` : ''}
                  </p>
                )}
                {source === 'shoutouts' && it.extra?.message && (
                  <p className="text-gray-400 text-xs mt-1 italic line-clamp-2">
                    “{it.extra.message}”
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="text-[10px] text-gray-500 font-['JetBrains_Mono']">
                {formatWhen(it.created_at)}
              </span>
              {source === 'requests' && it.extra?.status && (
                <span
                  className={`text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
                    it.extra.status === 'approved'
                      ? 'bg-[#39FF14]/15 text-[#39FF14]'
                      : it.extra.status === 'rejected'
                        ? 'bg-red-500/15 text-red-400'
                        : 'bg-yellow-500/15 text-yellow-400'
                  }`}
                >
                  {it.extra.status}
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

// ---------- Events grouped ----------
const EventGroups = ({ groups, openId, onToggle }) => {
  if (!groups.length) {
    return <EmptyState title="No events yet" body="Create an event in the Events section to start collecting RSVPs." />;
  }
  return (
    <ul className="space-y-2">
      {groups.map((g) => {
        const isOpen = g.event.id === openId;
        return (
          <li
            key={g.event.id}
            className="rounded-xl bg-white/[0.03] border border-white/8 overflow-hidden"
            data-testid={`event-group-${g.event.id}`}
          >
            <button
              onClick={() => onToggle(g.event.id)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors text-left"
              data-testid={`event-group-toggle-${g.event.id}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-lg bg-[#9D00FF]/15 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-[#9D00FF]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-bold truncate font-['Outfit']">{g.event.name || 'Untitled event'}</p>
                  <p className="text-[11px] text-gray-500 font-['JetBrains_Mono']">
                    {g.event.event_date}
                    {!g.event.is_all_day && g.event.start_time ? ` · ${g.event.start_time}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-white/70 font-mono">
                  <span className="text-[#9D00FF] font-bold">{g.stats.total}</span>
                  <span className="text-gray-600"> · </span>
                  <span className="text-[#39FF14]">{g.stats.checked_in}</span>
                  <span className="text-gray-600"> in</span>
                </span>
                <span className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-white/5 px-3 py-2 space-y-1.5 bg-black/40">
                {g.registrations.length === 0 ? (
                  <p className="text-xs text-gray-500 italic text-center py-3">No sign-ups yet</p>
                ) : (
                  g.registrations.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-white/[0.03]"
                      data-testid={`event-reg-${r.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-md bg-[#9D00FF]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#9D00FF]">
                            {(r.name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">{r.name || 'Unknown'}</p>
                          {r.phone && (
                            <a
                              href={`https://wa.me/${cleanPhoneForWa(r.phone)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] text-[#25D366] hover:underline font-['JetBrains_Mono']"
                            >
                              <Phone className="w-3 h-3" />{r.phone}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {r.is_checked_in ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#39FF14]/15 text-[#39FF14] uppercase tracking-wider font-bold">
                            <CheckCircle2 className="w-3 h-3" /> in
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-gray-400 uppercase tracking-wider">
                            <Clock className="w-3 h-3" /> awaited
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const EmptyState = ({ title, body }) => (
  <div className="flex flex-col items-center text-center py-16">
    <div className="w-14 h-14 rounded-full bg-white/[0.04] flex items-center justify-center mb-4">
      <ShieldOff className="w-6 h-6 text-gray-500" />
    </div>
    <p className="text-white text-base font-semibold mb-1">{title}</p>
    <p className="text-gray-500 text-sm max-w-xs">{body}</p>
  </div>
);

// Strip non-digits + leading "+" so wa.me links work uniformly
const cleanPhoneForWa = (raw) => String(raw || '').replace(/\D/g, '');
// Don't leak full IP — show first 3 octets only
const maskIp = (raw) => {
  const s = String(raw || '');
  if (!s) return '';
  if (s.includes(':')) {
    // IPv6 — show first 2 segments
    const parts = s.split(':').filter(Boolean);
    return `${parts.slice(0, 2).join(':')}::*`;
  }
  const parts = s.split('.');
  if (parts.length === 4) return `${parts.slice(0, 3).join('.')}.*`;
  return s;
};

export default CustomerDetailsModal;
